/**
 * confirmProxy.js — 確認對話框代理模組
 *
 * 【異動說明】
 * Vue 3 的 PrimeVue ConfirmationService 使用 useConfirm() composable，
 * 只能在 Vue 元件的 setup() 函式中呼叫。
 * 但我們的 axios 攔截器（request.js）不在元件生命週期內，
 * 無法直接使用 useConfirm()。
 *
 * 解決方案：Singleton Proxy 模式
 * 1. App.vue 在 setup() 中呼叫 useConfirm() 取得 confirm 實例
 * 2. 透過 registerConfirm() 將實例註冊到此模組
 * 3. request.js 透過 requireConfirm() 使用確認對話框
 * 4. 若 confirm 尚未註冊（App 尚未掛載），提供安全退路
 *
 * 這避免了在 window 上掛載全域變數（Vue 2 時的 window.__app__），
 * 讓依賴關係更明確、更容易測試。
 */

// 儲存 PrimeVue confirm 實例的內部變數
let _confirm = null

/**
 * 註冊 PrimeVue confirm 實例
 * 由 App.vue 的 setup() 呼叫，將 useConfirm() 取得的實例傳入
 *
 * @param {Object} confirm - PrimeVue useConfirm() 回傳的實例
 */
export function registerConfirm(confirm) {
  _confirm = confirm
}

/**
 * 發起確認對話框請求
 * 包裝成 Promise，讓 axios 攔截器可以用 await 等待使用者操作結果
 *
 * @param {Object} opts - PrimeVue ConfirmDialog 的選項
 * @param {string} opts.message - 對話框訊息
 * @param {string} opts.header - 對話框標題
 * @param {string} opts.acceptLabel - 確認按鈕文字
 * @param {string} opts.rejectLabel - 取消按鈕文字
 * @returns {Promise<'accept'|'reject'>} 使用者的選擇結果
 */
export function requireConfirm(opts) {
  // 安全退路：若 App.vue 尚未掛載或 confirm 未註冊，直接回首頁
  if (!_confirm) {
    window.location.href = '/bill/index.html'
    return Promise.resolve('reject')
  }

  return new Promise((resolve) => {
    _confirm.require({
      ...opts,
      accept: () => resolve('accept'),
      reject: () => resolve('reject'),
    })
  })
}
